<template>
  <div class="role-page">
    <header class="role-hero">
      <div>
        <p class="eyebrow">Compte employé</p>
        <h1>Mes avances</h1>
        <p class="employee-name">{{ staffName }}</p>
        <p>Consultez les avances reçues et le mois de salaire concerné.</p>
      </div>
      <div class="month-field">
        <label class="form-label">Mois</label>
        <MonthPickerButton v-model="month" @change="fetchAdvances" />
      </div>
    </header>

    <section class="summary-grid">
      <div class="summary-card">
        <span>Total avances</span>
        <strong>{{ totalAmount }} DH</strong>
      </div>
      <div class="summary-card">
        <span>Nombre d'avances</span>
        <strong>{{ advances.length }}</strong>
      </div>
    </section>

    <section class="role-card">
      <div class="list-head">
        <h2>Détail</h2>
        <button class="btn-secondary" @click="fetchAdvances">Actualiser</button>
      </div>
      <div v-if="loading" class="empty-state">Chargement...</div>
      <div v-else-if="!advances.length" class="empty-state">Aucune avance pour ce mois.</div>
      <div v-else class="advance-list">
        <article v-for="item in advances" :key="item.id" class="advance-row">
          <div>
            <strong>{{ Math.round(item.avance) }} DH</strong>
            <span>{{ formatDateTime(item.created_at) }}</span>
          </div>
          <small>Donné par {{ giverLabel(item) }} · Déduit de {{ formatMonth(item.salary_month || month) }}</small>
        </article>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </section>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  name: 'AppEmployeeAdvances',
  components: { MonthPickerButton },
  data() {
    return {
      month: new Date().toISOString().slice(0, 7),
      staff: null,
      advances: [],
      loading: false,
      error: '',
    };
  },
  computed: {
    totalAmount() {
      return Math.round(this.advances.reduce((sum, item) => sum + Number(item.avance || 0), 0));
    },
    staffName() {
      return this.staff?.full_name || 'Mon compte';
    },
  },
  mounted() {
    this.fetchAdvances();
  },
  methods: {
    async readError(res, fallback) {
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        return data.error || fallback;
      } catch {
        return text || fallback;
      }
    },
    async fetchAdvances() {
      this.loading = true;
      this.error = '';
      try {
        const res = await fetch(`/api-pointage/my-advances?month=${this.month}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(await this.readError(res, 'Chargement impossible'));
        const data = await res.json();
        if (Array.isArray(data)) {
          this.advances = data;
          return;
        }
        this.staff = data?.staff || null;
        this.advances = Array.isArray(data?.items) ? data.items : [];
      } catch (err) {
        this.error = err.message;
        this.advances = [];
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '';
      const normalized = String(value).replace('/', '-').replace('/', '-').replace(' ', 'T');
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatMonth(value) {
      if (!value) return '-';
      const [year, month] = String(value).split('-');
      if (!year || !month) return value;
      return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric',
      });
    },
    giverLabel(item) {
      if (item.created_by_role === 'caissier') return item.created_by_name || 'Caissier';
      return 'Admin';
    },
  },
};
</script>

<style scoped>
.role-page {
  width: min(960px, calc(100vw - 32px));
  margin: 28px auto;
}
.role-hero,
.role-card,
.summary-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
.role-hero {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 24px;
  margin-bottom: 16px;
}
.eyebrow,
.form-label {
  color: var(--primary);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}
h1,
h2,
p {
  margin: 0;
}
.role-hero h1 {
  font-size: 28px;
  margin: 6px 0;
}
.role-hero p {
  color: var(--gray-600);
}
.employee-name {
  margin-bottom: 6px;
  color: var(--gray-900) !important;
  font-size: 16px;
  font-weight: 800;
}
.month-field {
  min-width: 210px;
}
.form-label {
  display: block;
  color: var(--gray-700);
  margin-bottom: 8px;
}
.form-input {
  min-height: 44px;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 14px;
  font: inherit;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}
.summary-card {
  padding: 18px;
}
.summary-card span {
  display: block;
  color: var(--gray-600);
  margin-bottom: 8px;
}
.summary-card strong {
  font-size: 28px;
}
.role-card {
  padding: 0;
  overflow: hidden;
}
.list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  border-bottom: 1px solid var(--border);
}
.btn-secondary {
  min-height: 40px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--gray-50);
  padding: 0 14px;
  font-weight: 800;
}
.advance-list {
  display: grid;
}
.advance-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
}
.advance-row:last-child {
  border-bottom: 0;
}
.advance-row strong,
.advance-row span {
  display: block;
}
.advance-row span,
.advance-row small {
  color: var(--gray-600);
}
.empty-state {
  padding: 32px;
  text-align: center;
  color: var(--gray-600);
}
.error {
  color: var(--danger);
  font-weight: 800;
  padding: 0 18px 18px;
}
@media (max-width: 700px) {
  .role-page {
    width: 100%;
    margin: 0 auto;
    display: grid;
    gap: 12px;
  }

  .role-hero,
  .role-card,
  .summary-card {
    border-radius: 10px;
  }

  .role-hero {
    padding: 18px 16px;
    margin-bottom: 0;
    gap: 14px;
  }

  .role-hero h1 {
    font-size: 26px;
    margin: 4px 0 6px;
  }

  .role-hero p {
    line-height: 1.45;
  }

  .month-field {
    min-width: 0;
    width: 100%;
  }

  .summary-grid {
    gap: 10px;
    margin-bottom: 0;
  }

  .summary-card {
    padding: 16px;
  }

  .summary-card strong {
    font-size: 26px;
  }

  .list-head {
    padding: 14px 16px;
  }

  .list-head h2 {
    font-size: 18px;
  }

  .btn-secondary {
    min-height: 36px;
    padding: 0 12px;
  }

  .role-hero,
  .advance-row {
    flex-direction: column;
  }

  .advance-row {
    padding: 15px 16px;
    gap: 8px;
  }

  .advance-row strong {
    font-size: 22px;
  }

  .advance-row small {
    line-height: 1.45;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
